<template>
  <NuxtLayout class="app">
    <NuxtLoadingIndicator color="#00A3A3" :height="4" />
    <NuxtPage :transition="pageTransition" />
  </NuxtLayout>
  <!-- eslint-disable-next-line vue/no-multiple-template-root -- lightbox must sit outside the layout -->
  <Lightbox />
</template>

<script setup lang="ts">
// always load settings first
import { onMounted } from 'vue'
import { preloadComponents } from 'nuxt/app'
import { useSettingsStore } from './store/settings'
import { useBrowserSupportStore } from '~/store/browser-support'
import { useInteractionStore } from '~/store/interaction'
import { onUnmounted } from '#imports'
import { useAnimatedTitleStore } from '~/store/animated-title'
import { loadScripts } from '~/utils/scripts'
import Lightbox from '~/components/layouts/Lightbox.vue'

// Safety net for leave transitions that never finish (e.g. navigating while the
// previous page is still loading): once the new page has entered, drop any
// stale page roots left behind next to it.
const pageTransition = {
  name: 'page',
  mode: 'out-in' as const,
  onAfterEnter: (el: Element) => {
    Array.from(el.parentElement?.children ?? []).forEach((sibling) => {
      if (sibling !== el && !sibling.classList.contains('nuxt-loading-indicator')) {
        sibling.remove()
      }
    })
  }
}

const settingsStore = useSettingsStore()
const browserSupportStore = useBrowserSupportStore()
const interactedStore = useInteractionStore()
const animatedTitleStore = useAnimatedTitleStore()

await preloadComponents([
  'AppBreakpointListener',
  'AppLoadingIndicator',
  'AppHeadingResize',
  'AppHomeHero',
  'AppHompageHero',
  'AppArticleHero',
  'AppContentHero',
  'AppArchiveHero',
  'AppLongreadHero',
  'AppProgramHero',
  'AppPersonHero',
  'AppVideoHero'
])

// listen for fonts loaded event.
if (process.client) {
  animatedTitleStore.listenForFontsLoaded()
}

// get all data needed for this page
await Promise.all([settingsStore.loadSettings()])

loadScripts()

onMounted(() => {
  browserSupportStore.setNativeShareSupported()
  interactedStore.setListeners()
  browserSupportStore.setIsMobile()
})

onUnmounted(() => {
  interactedStore.clearListeners()
})

/*
  When all else fails: refresh the page if something goes wrong.
  this triggers an SSR version of the page, which might solve the issue.
  This is our best bet at always generating a working page.
*/
// if (process.client && !process.dev) {
//   window.onerror = function () {
//     location.reload()
//   }
// }
</script>

<style lang="postcss">
/* from */
.page-leave-active {
  transition: opacity 0.35s linear 0.25s;
  opacity: 1;

  img {
    opacity: 1;
    transition: opacity 0.2s linear;
  }
}

/* the tipping point */
.page-leave-to,
.page-enter-from {
  opacity: 0;
}

/* to */
.page-enter-active {
  transition: opacity 0.3s linear;
}

.page-enter-to {
  opacity: 1;
}
</style>
